// Vendor-wise (consignment) reports under Store Inventory (F-RPT-N7 Phase 2).
// Pick a vendor + date range, then read their Sales and Stock reports on screen
// and export EACH separately as .xlsx — matching the per-vendor statement a
// consignment supplier expects. Vendor attribution flows through the existing
// inventory link: a menu item's tracked stock item carries the supplier.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, FileSpreadsheet, RefreshCw, CheckCircle2, Receipt } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useReportRange } from '@/hooks/useReportRange'
import { DateRangeFilter } from '@/components/admin/reports/DateRangeFilter'
import { useCurrency } from '@/contexts/CurrencyContext'
import { cn } from '@/lib/utils'
import type { Supplier } from '@/types'

export function VendorReportsTab({ showToast }: { showToast: (t: 'success' | 'error', m: string) => void }) {
  const { formatCurrency } = useCurrency()
  const range = useReportRange()
  const [vendorId, setVendorId] = useState<string>('')
  const [exporting, setExporting] = useState<'sales' | 'stock' | null>(null)

  const { data: suppliersRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers: Supplier[] = suppliersRes?.data ?? []

  const salesQuery = useQuery({
    queryKey: ['vendor-sales', vendorId, range.fromISO, range.toISO],
    queryFn: () => apiClient.getVendorSalesReport(vendorId, range.fromISO, range.toISO).then(r => r.data),
    enabled: !!vendorId,
  })
  const stockQuery = useQuery({
    queryKey: ['vendor-stock', vendorId],
    queryFn: () => apiClient.getVendorStockReport(vendorId).then(r => r.data),
    enabled: !!vendorId,
  })

  const sales = salesQuery.data
  const stock = stockQuery.data
  const commission = sales?.commission_percent ?? suppliers.find(s => s.id === vendorId)?.commission_percent ?? 0
  const vendorShare = useMemo(() => Math.round((100 - commission) * 100) / 100, [commission])
  const [settling, setSettling] = useState(false)

  const generateSettlement = async () => {
    if (!vendorId) return
    setSettling(true)
    try {
      const res = await apiClient.createVendorSettlement(vendorId, range.fromISO, range.toISO)
      const amt = res.data?.settlement?.amount ?? sales?.total_vendor_payout ?? 0
      showToast('success', res.data?.already_existed
        ? `Already settled: Rs ${amt.toLocaleString()}`
        : `Settlement bill created: Rs ${amt.toLocaleString()} — see Vendors / AP`)
      await salesQuery.refetch()
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Failed to create settlement')
    } finally {
      setSettling(false)
    }
  }

  const exportReport = async (report: 'sales' | 'stock') => {
    if (!vendorId) return
    setExporting(report)
    try {
      await apiClient.exportVendorReportXlsx(report, vendorId, range.fromISO, range.toISO)
    } catch {
      showToast('error', `Failed to export ${report} report`)
    } finally {
      setExporting(null)
    }
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1.5 max-w-3xl">
          <h3 className="text-lg font-semibold">Vendor reports</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Per-vendor sales &amp; stock for consignment suppliers. Sales are attributed through each
            menu item's linked inventory item; the commission split comes from the vendor's
            configured percentage. Export each report separately.
          </p>
        </div>
      </div>

      {/* Vendor + date-range controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">Vendor</span>
          <Select value={vendorId} onValueChange={setVendorId}>
            <SelectTrigger className="w-64 h-9">
              <SelectValue placeholder="Select a vendor…" />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.commission_percent ? ` · ${s.commission_percent}%` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DateRangeFilter range={range} />
      </div>

      {!vendorId ? (
        <div className="rounded-lg border border-dashed bg-muted/20 p-10 text-center text-sm text-muted-foreground">
          Pick a vendor to see their sales and stock reports.
        </div>
      ) : (
        <>
          {/* ── Sales report ─────────────────────────────────────── */}
          <div className="rounded-lg border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
              <div>
                <h4 className="font-semibold">Sales report</h4>
                <p className="text-xs text-muted-foreground">
                  Vendor gets {vendorShare}% · house keeps {commission}%
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Consignment settlement: turn the period's payout into a payable
                    in Vendors/AP. Shows the existing bill once settled. */}
                {sales?.is_consignment && (sales?.total_vendor_payout ?? 0) > 0 && (
                  sales?.settlement ? (
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Settled Rs {sales.settlement.amount.toLocaleString()} ({sales.settlement.status})
                    </span>
                  ) : (
                    <Button size="sm" className="gap-2" disabled={settling} onClick={generateSettlement}>
                      {settling ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                      Generate settlement bill
                    </Button>
                  )
                )}
                <Button size="sm" variant="outline" className="gap-2" disabled={exporting !== null} onClick={() => exportReport('sales')}>
                  {exporting === 'sales' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                  Export Excel
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              {salesQuery.isLoading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
              ) : !sales || sales.items.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No sales for this vendor in the selected range.</div>
              ) : (
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5 text-left">Item</th>
                      <th className="px-4 py-2.5 text-right">Qty sold</th>
                      <th className="px-4 py-2.5 text-right">Avg unit price</th>
                      <th className="px-4 py-2.5 text-right">Gross sales</th>
                      <th className="px-4 py-2.5 text-right">House {commission}%</th>
                      <th className="px-4 py-2.5 text-right">Vendor payout</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sales.items.map((r, i) => (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-4 py-2.5">{r.item_name}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{r.qty_sold.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{formatCurrency(r.avg_unit_price)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.gross_sales)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{formatCurrency(r.house_cut)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{formatCurrency(r.vendor_payout)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t bg-muted/30 font-semibold">
                    <tr>
                      <td className="px-4 py-2.5">Total</td>
                      <td className="px-4 py-2.5" />
                      <td className="px-4 py-2.5" />
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(sales.total_gross)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(sales.total_house_cut)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(sales.total_vendor_payout)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>

          {/* ── Stock report ─────────────────────────────────────── */}
          <div className="rounded-lg border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
              <div>
                <h4 className="font-semibold">Stock report</h4>
                <p className="text-xs text-muted-foreground">Live units on hand of this vendor's inventory items</p>
              </div>
              <Button size="sm" variant="outline" className="gap-2" disabled={exporting !== null} onClick={() => exportReport('stock')}>
                {exporting === 'stock' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Export Excel
              </Button>
            </div>
            <div className="overflow-x-auto">
              {stockQuery.isLoading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
              ) : !stock || stock.items.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No inventory items linked to this vendor.</div>
              ) : (
                <table className="w-full min-w-[560px] text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5 text-left">Item</th>
                      <th className="px-4 py-2.5 text-left">Unit</th>
                      <th className="px-4 py-2.5 text-right">Qty on hand</th>
                      <th className="px-4 py-2.5 text-right">Unit cost</th>
                      <th className="px-4 py-2.5 text-right">Stock value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {stock.items.map((r, i) => (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-4 py-2.5">
                          {r.item_name}
                          {r.oversold && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                              oversold — reconcile
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{r.unit}</td>
                        <td className={cn('px-4 py-2.5 text-right tabular-nums', r.oversold && 'text-amber-700 dark:text-amber-400')}>{r.qty_on_hand.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{formatCurrency(r.unit_cost)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.stock_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t bg-muted/30 font-semibold">
                    <tr>
                      <td className="px-4 py-2.5">Total</td>
                      <td className="px-4 py-2.5" />
                      <td className="px-4 py-2.5 text-right tabular-nums">{stock.total_qty.toLocaleString()}</td>
                      <td className="px-4 py-2.5" />
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(stock.total_value)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
